import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Sector } from '../../lib/models/fundamental/types';
import { getAllSectors } from '../../lib/services/fundamental/helpers';
import { RootState } from '../../lib/store';
import { getETFs, getSectors } from '../../lib/store/fundamental';
import { DEFAULT_PAGE, GET_FIRST_ITEM_SIZE } from '../../lib/store/fundamental/constants';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import {
  isCallStatusError,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import PopularGroups from '../../shared_components/PopularGroups/PopularGroups';
import { PopularGroupItem } from '../../shared_components/PopularGroups/PopularGroups.types';
import { UI_ROUTES } from '../../util/routes';

import groups from './PopularGroupItems/groups';

const PopularGroupsWrapper = () => {
  const sectors = useSelector<RootState, Sector[]>((state: RootState) => getAllSectors(state));
  const isGetETFReady = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusReady(FundamentalCall.getETFs, state.fundamental.statusByCall));
  const isGetETFError = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusError(FundamentalCall.getETFs, state.fundamental.statusByCall));
  const isGetSectorReady = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusReady(FundamentalCall.getSectors, state.fundamental.statusByCall));
  const isGetSectorError = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusError(FundamentalCall.getSectors, state.fundamental.statusByCall));
  const isLoadingSectorOrETF: boolean = (!isGetETFReady && !isGetETFError) || (!isGetSectorReady && !isGetSectorError);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getETFs({ size: GET_FIRST_ITEM_SIZE, page: DEFAULT_PAGE }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(getSectors());
  }, [dispatch]);

  // Ordered by the local group manifest rather than by the API response.
  const groupItems = useMemo((): PopularGroupItem[] => (
    Object.values(groups).flatMap((group) => {
      const sector = sectors.find(item => item.sector === group.sectorName);
      if (!sector) return [];

      return [{
        id: sector.id,
        sector: sector.sector,
        name: group.name,
        logo: group.logo,
        shortLogo: group.shortLogo,
        count: sector.symbolCount,
      }];
    })
  ), [sectors]);

  const handleGroupClick = (item: PopularGroupItem) => {
    const group = sectors.find(sector => sector.sector === item.sector);
    navigate(
      { pathname: `${UI_ROUTES.discover}${UI_ROUTES.popularGroup}/${item.sector}` },
      { state: { group, sector: item.name } },
    );
  };

  return (
    <PopularGroups
      groups={groupItems}
      isLoading={isLoadingSectorOrETF}
      showEtf
      onGroupClick={handleGroupClick}
    />
  );
};

export default PopularGroupsWrapper;
